import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CART':
      return {
        ...state,
        items: action.payload || []
      };

    case 'ADD_TO_CART': {
      const existingItem = state.items.find(item => item.id === action.payload.id && item.variantIndex === action.payload.variantIndex);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id && item.variantIndex === action.payload.variantIndex
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: 1 }]
        };
      }
    }

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => !(item.id === action.payload.id && item.variantIndex === action.payload.variantIndex))
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id && item.variantIndex === action.payload.variantIndex
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item
        ).filter(item => item.quantity > 0)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  // Initialize state with data from localStorage
  const getInitialState = () => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        if (parsedCart && parsedCart.items && Array.isArray(parsedCart.items)) {
          console.log('Loading cart from localStorage:', parsedCart.items);
          return { items: parsedCart.items };
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      localStorage.removeItem('cart');
    }
    return { items: [] };
  };

  const [state, dispatch] = useReducer(cartReducer, getInitialState());
  const isInitialized = useRef(false);

  // Save cart to localStorage whenever it changes (but not during initial load)
  useEffect(() => {
    if (isInitialized.current) {
      console.log('Saving cart to localStorage:', state);
      localStorage.setItem('cart', JSON.stringify(state));
    } else {
      isInitialized.current = true;
    }
  }, [state]);

  const addToCart = (product) => {
    console.log('Adding to cart:', product);
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId, variantIndex) => {
    console.log('Removing from cart:', productId, variantIndex);
    dispatch({ type: 'REMOVE_FROM_CART', payload: { id: productId, variantIndex } });
  };

  const updateQuantity = (productId, variantIndex, quantity) => {
    console.log('Updating quantity:', productId, variantIndex, quantity);
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, variantIndex, quantity } });
  };

  const clearCart = () => {
    console.log('Clearing cart');
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (parseFloat(item.price) * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 